import React, { useState } from "react";
import { AlertTriangle, ChevronDown } from "lucide-react";

const SECTIONS = [
  {
    id: "s1",
    title: "1. Parties, Definitions and Application",
    body: [
      "These Terms apply to every contract of carriage of Goods and Dangerous Goods entered into by McCallum Bros Limited (\"the Carrier\") for voyages between the Chatham Islands and mainland New Zealand, and prevail over any conflicting terms proposed by the Customer, whether in a purchase order, Booking form, or otherwise, unless expressly agreed in writing by the Carrier.",
      "These Terms are separate from, and do not affect, any general terms of trade published by the Carrier or its related companies for the supply of goods and services unrelated to carriage (for example, aggregate, concrete, and equestrian surfacing products).",
      "Nothing in these Terms limits the Carrier's rights or obligations under the Contract and Commercial Law Act 2017 (CCLA), the Maritime Transport Act 1994, or any other applicable Laws, except to the extent contracting out is expressly permitted by law and is recorded in these Terms.",
    ],
  },
  {
    id: "s2",
    title: "2. Booking",
    body: [
      "All Goods must be booked with the Carrier in advance using the Carrier's online booking portal.",
      "The Carrier may refuse to load any Goods that are not properly manifested, packaged, or declared, without liability to the Carrier.",
    ],
  },
  {
    id: "s3",
    title: "3. Freight Rates and Charges",
    body: [
      "Freight and charges are payable in accordance with Schedule 1, exclusive of GST.",
      "Rates include a Bunker Adjustment Factor (BAF), which the Carrier will disclose and calculate transparently on each invoice.",
      "The inter-island charge set out in Schedule 1 applies only to Goods moving directly between Chatham Island and Pitt Island, and does not apply to carriage between either island and a mainland New Zealand port.",
      "The Carrier may vary its rates on reasonable notice to reflect increases in port charges, regulatory costs, or other costs beyond its control. Variations will be notified to the Customer in writing and will not apply retrospectively to Bookings already accepted.",
    ],
  },
  {
    id: "s4",
    title: "4. Payment Terms and Credit",
    body: [
      "All payments must be made in full, without deduction or set-off, by the due date stated on the Carrier's invoice. Unless otherwise agreed in writing, all freight and charges estimated by the Carrier prior to loading must be paid in full by the Customer before loading on board the Vessel.",
      "If payment is not made by the due date, the Carrier may charge interest on the overdue balance at 5% per annum above the rate charged by the Carrier's bank, from the due date until actual payment.",
      "The Carrier may, on 5 days' written notice, suspend the Shipping Service to the Customer until all overdue amounts are paid in full.",
      "Where the Carrier reasonably considers the Customer's credit to be unsatisfactory, it may require the Customer to provide security for payment before accepting further Bookings.",
      "The Carrier has sole discretion to apply any payment received from the Customer to any invoice or transaction, regardless of any instruction from the Customer to the contrary.",
      "Freight is deemed earned by the Carrier upon loading of the Goods onto the Vessel.",
    ],
  },
  {
    id: "s5",
    title: "5. Dangerous Goods",
    body: [
      "The Customer must declare any Dangerous Goods to the Carrier at the time of Booking, including full details of classification, quantity, and packaging.",
      "Dangerous Goods must be packaged, labelled, and documented in accordance with the IMDG Code, the Land Transport Rule: Dangerous Goods 2005 (where applicable), and Maritime Rules Part 24A.",
      "The Carrier may refuse to accept any Dangerous Goods that are not declared or that do not comply with the above.",
      "The Customer indemnifies the Carrier for all loss, claims, damage, delay, fines, or expenses arising from a failure to declare Dangerous Goods, or from non-compliant packaging or documentation.",
    ],
  },
  {
    id: "s6",
    title: "6. Carrier's Lien and Remedies for Non-Payment",
    body: [
      "The Carrier has a lien over Goods carried under these Terms in respect of unpaid freight and charges, in accordance with sections 283–292 of the CCLA, and may, after giving the notice required by that Act, sell any Goods held under lien by public auction to recover amounts owing.",
    ],
  },
  {
    id: "s7",
    title: "7. Customer Warranties and Responsibilities",
    body: [
      "The Customer warrants that all information provided in any Booking, manifest, or declaration is true, complete, and not misleading.",
      "The Customer warrants that the Goods are packaged for sea carriage, and that all Dangerous Goods are declared and packaged in accordance with Section 5.",
      "The Customer is responsible for arranging safe and timely delivery of Goods to the relevant Port, and for their prompt collection following discharge.",
      "The Customer indemnifies the Carrier against any loss, claim, cost, or claim arising from a breach of the warranties in this section.",
    ],
  },
  {
    id: "s8",
    title: "8. Regulatory Compliance",
    body: [
      "The Customer warrants that all Goods presented for carriage comply with all applicable laws, regulations, permits, approvals and directions relating to biosecurity, food safety, customs, health and safety, dangerous goods, maritime transport and any other regulatory requirements — and that all declarations, permits, certificates and information required by any authority are complete, accurate, and provided before loading.",
      "The Carrier may refuse to accept, load, carry, unload, isolate, inspect, treat, destroy, dispose of or otherwise deal with any Goods where it reasonably considers this necessary to comply with the law, comply with a regulator's direction, or protect the Vessel, other cargo, persons or the environment from biosecurity, contamination or regulatory risk — without liability, except where caused intentionally by the Carrier.",
      "The Customer indemnifies the Carrier against all loss, liability, cost, expense, penalty, fine, delay and claim arising from a breach of this section, any inaccuracy in information provided, any actual or suspected biosecurity risk associated with the Goods, or any regulatory action affecting the Goods.",
      "Any additional costs incurred by the Carrier as a result of compliance with this section — inspection, cleaning, fumigation, quarantine, storage, handling, disposal, transport or regulatory charges — are recoverable from the Customer as a debt due and payable.",
    ],
  },
  {
    id: "s9",
    title: "9. Consumer Guarantees Act 1993",
    body: [
      "The Customer acknowledges that Goods are carried under these Terms for the purposes of a business, and the parties agree, under section 43(2) of the Consumer Guarantees Act 1993, that the Act does not apply to carriage under these Terms.",
      "This clause takes effect only to the extent it is fair and reasonable, having regard to the parties' relative bargaining strength and whether the Customer received legal advice before agreeing to these Terms.",
    ],
  },
  {
    id: "s10",
    title: "10. Force Majeure and Schedule Variation",
    body: [
      "The Carrier is not liable for any failure or delay caused by weather, sea or tidal conditions, mechanical breakdown, war, civil commotion, strike, act of God, fire, government regulation or direction, or any other cause beyond its reasonable control.",
      "Sailing dates, arrival/departure times, transit times and delivery estimates are estimates only and are not guaranteed — time is not of the essence. The Carrier is not liable for loss, damage, expense or delay from postponement or cancellation of a Voyage, delays in loading/sailing/transit/discharge, weather or sea conditions, port congestion, vessel mechanical issues, the Master's safety discretion, or compliance with any regulatory direction.",
      "A delay or interruption to a Voyage does not entitle the Customer to cancel a Booking, withhold payment, recover consequential/indirect loss, or make a claim against the Carrier, except where liability cannot lawfully be excluded.",
      "The Carrier may alter the loading order, discharge order, ports of call, routing or scheduling, or postpone or cancel a Voyage, where reasonably necessary for operational, safety or commercial reasons.",
      "The Carrier will publish an allocation policy for Bookings during peak demand, applying in place of strict first-come-first-served where necessary to manage capacity fairly.",
    ],
  },
  {
    id: "s11",
    title: "11. Himalaya Clause",
    body: [
      "Every exemption, limitation, defence, immunity and indemnity available to the Carrier under these Terms also applies to the Carrier's personnel — officers, employees, the Master, crew, contractors and agents — as if they were a party to these Terms.",
    ],
  },
  {
    id: "s12",
    title: "12. Liberty of the Carrier",
    body: [
      "The Carrier and the Master may, without liability and without it being treated as a breach of contract: postpone, delay or cancel a Voyage; deviate from the usual route; call at ports in any order; substitute another vessel; transfer or tranship Goods; load, stow or restow Goods as considered appropriate; refuse or discontinue carriage where necessary for safety or legal compliance; and take any other action reasonably necessary for the safety of the Vessel, crew, or Goods.",
      "Any additional costs the Carrier reasonably incurs as a result — extra handling, storage, transport or port charges attributable to the Customer's Goods — are payable by the Customer.",
    ],
  },
  {
    id: "s13",
    title: "13. Master's Authority",
    body: [
      "The Master has absolute discretion over navigation, operation and safety of the Vessel — including loading, stowage, carriage, discharge, routing, weather avoidance, and compliance with the law. Exercising this discretion is not a breach of these Terms and does not give rise to a claim, except where liability cannot lawfully be excluded.",
    ],
  },
  {
    id: "s14",
    title: "14. Delivery and Collection",
    body: [
      "Unless otherwise agreed in writing, the Carrier's responsibility for Goods ends when they are discharged from the Vessel or made available for collection at the nominated Port.",
      "The Customer must promptly collect Goods once notified. If they fail to do so, the Carrier may, at the Customer's risk and expense, store the Goods, arrange third-party holding, relocate them, or take other reasonable protective steps. All resulting costs are recoverable from the Customer as a debt.",
      "If Goods remain uncollected for more than 15 days after notification, the Carrier may, after giving at least 5 days' written notice (where practicable), sell or dispose of the Goods and apply proceeds to amounts owing, accounting to the Customer for any surplus.",
    ],
  },
  {
    id: "s15",
    title: "15. General Average and Salvage",
    body: [
      "General average is adjusted and settled under the York-Antwerp Rules 2016 (or any later revision), except where inconsistent with New Zealand law.",
      "The Customer must contribute, in proportion to the value of its Goods, to any general average sacrifice or expenditure incurred for the common safety of the Vessel and cargo, and is liable for its proportion of any salvage award or related maritime charges.",
      "The Carrier may require a cash deposit, guarantee, bond or other security before releasing Goods, and is not obliged to release Goods until the Customer has complied.",
    ],
  },
  {
    id: "s16",
    title: "16. Claims Process",
    body: [
      "Any claim for loss of, or damage to, Goods must be notified to the Carrier in writing, with reasonable particulars, within 30 days of the Carrier's responsibility for the Goods ending.",
      "The Carrier is not liable for any claim not notified within that period, or for any loss that is consequential, indirect, or economic in nature.",
    ],
  },
  {
    id: "s17",
    title: "17. Termination",
    body: [
      "The Carrier may terminate a Booking or contract of carriage immediately, without liability, if the Customer becomes insolvent or enters administration, liquidation or receivership.",
      "Termination does not entitle the Customer to a refund of amounts already paid, and does not relieve the Customer of amounts owing or liability for breaches occurring before termination.",
    ],
  },
  {
    id: "s18",
    title: "18. Limitation of Liability",
    body: [
      "The limitation in this clause applies regardless of the number of claims made, causes of action, claimants, or legal proceedings arising from the same Booking, Voyage or occurrence.",
      "The Customer acknowledges that freight charged has been determined based on the exclusions and limitations of liability in these Terms, and that the Customer has had the opportunity to insure the Goods.",
    ],
  },
  {
    id: "s19",
    title: "19. Dispute Resolution",
    body: [
      "Any dispute arising from these Terms is first referred to mediation, on a shared-cost basis, with a mediator agreed between the parties. If unresolved, it proceeds to arbitration under the Arbitration Act 1996, before an arbitrator agreed between the parties.",
    ],
  },
  {
    id: "s20",
    title: "20. No Waiver",
    body: [
      "No failure, delay or omission by the Carrier in exercising any right operates as a waiver of that right. A waiver is only effective if in writing and signed by an authorised representative, and applies only to the specific matter given.",
    ],
  },
  {
    id: "s21",
    title: "21. Severability",
    body: [
      "If any provision of these Terms is held illegal, invalid or unenforceable, it will be severed without affecting the remaining provisions, which continue in full force and effect.",
    ],
  },
  {
    id: "s22",
    title: "22. Assignment",
    body: [
      "The Customer must not assign, transfer or subcontract its rights or obligations under these Terms without the Carrier's prior written consent. The Carrier may assign or transfer its rights to a related company or another carrier without the Customer's consent, provided this doesn't relieve it of any liability that can't lawfully be excluded.",
    ],
  },
  {
    id: "s23",
    title: "23. Entire Agreement",
    body: [
      "These Terms constitute the entire agreement between the Carrier and the Customer regarding carriage of Goods, superseding all prior negotiations and understandings. No variation is effective unless made in writing and approved by the Carrier.",
    ],
  },
  {
    id: "s24",
    title: "24. Governing Law",
    body: [
      "These Terms are governed by the laws of New Zealand, and the parties submit to the exclusive jurisdiction of the courts of Auckland, New Zealand.",
    ],
  },
];

function Section({ section }) {
  const [open, setOpen] = useState(true);
  return (
    <section id={section.id} className="scroll-mt-24 rounded-2xl border border-slate-200 bg-white overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-slate-50"
      >
        <h2 className="text-base font-semibold text-slate-950">{section.title}</h2>
        <ChevronDown size={18} className={`shrink-0 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 space-y-3">
          {section.flag && (
            <div className="flex items-start gap-2.5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <span>{section.flag}</span>
            </div>
          )}
          {section.body.map((para, i) => (
            <p key={i} className="text-sm leading-relaxed text-slate-600">
              {para}
            </p>
          ))}
        </div>
      )}
    </section>
  );
}

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-4xl px-6 py-12 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">McCallum Bros Limited</p>
          <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-950">
            Terms of Trade for Carriage of Freight
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            Chatham Islands – Mainland New Zealand Shipping Service
          </p>
        </div>


        {/* Sections */}
        <div className="space-y-4">
          {SECTIONS.map((section) => (
            <Section key={section.id} section={section} />
          ))}
        </div>

        <p className="mt-10 text-center text-xs text-slate-400">
          McCallum Bros Limited · NZBN 9429040749303 · Questions about these terms? Contact{" "}
          <a href="mailto:dispatch@mccallumbros.co.nz" className="underline hover:text-slate-600">
            dispatch@mccallumbros.co.nz
          </a>
        </p>
      </div>
    </div>
  );
}